import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "./PageLink";
import { pagePath } from "../lib/routes";
import logoSrc from "@assets/Logo_J&S_500_trs_1778808372699.png";

import { CLUSTERS, SERVICES } from "../lib/areas";

const STATIC_PAGE_TITLES: Record<string, string> = {
  "/contact": "Contact",
  "/privacy": "Privacy Policy",
};

const NAV_SERVICES: { path: string; label: string; testId: string }[] = [
  { path: "/services/home-theater-systems", label: "Home Theater", testId: "link-nav-home-theater" },
  { path: "/services/sound-systems", label: "Sound Systems", testId: "link-nav-sound-systems" },
  { path: "/services/commercial-av-solutions", label: "Commercial AV", testId: "link-nav-commercial-av" },
  { path: "/services/residential-wifi-surveillance", label: "Wi-Fi & Surveillance", testId: "link-nav-wifi-surveillance" },
];

const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/profile.php?id=100063815122265",
  instagram: "https://www.instagram.com/jandsaudiovideo/",
  google: "https://maps.app.goo.gl/wxkWvzsUe3FXVT54A",
};

const PHONE_DISPLAY = "508-450-0490";
const PHONE_HREF = "tel:+15084500490";

const PhoneIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
  </svg>
);

const FacebookIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="18" cy="6" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const GoogleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.24 10.4V14.1h5.18c-.21 1.34-1.55 3.93-5.18 3.93-3.12 0-5.66-2.58-5.66-5.76s2.54-5.76 5.66-5.76c1.77 0 2.96.76 3.64 1.4l2.48-2.39C16.78 3.96 14.7 3 12.24 3 7.34 3 3.38 6.96 3.38 11.85S7.34 20.7 12.24 20.7c5.1 0 8.48-3.59 8.48-8.64 0-.58-.06-1.02-.14-1.46h-8.34z" />
  </svg>
);

export const SocialLinks: React.FC<{ variant: "header" | "footer" | "topbar" }> = ({ variant }) => (
  <div className={`social-links social-links--${variant}`}>
    <a
      href={SOCIAL_LINKS.facebook}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="J&S Audio Video Solutions on Facebook"
      data-testid={`link-${variant}-facebook`}
    >
      <FacebookIcon />
    </a>
    <a
      href={SOCIAL_LINKS.instagram}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="J&S Audio Video Solutions on Instagram"
      data-testid={`link-${variant}-instagram`}
    >
      <InstagramIcon />
    </a>
    <a
      href={SOCIAL_LINKS.google}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="J&S Audio Video Solutions on Google"
      data-testid={`link-${variant}-google`}
    >
      <GoogleIcon />
    </a>
  </div>
);

const getPageTitle = (pathname: string): string | null => {
  if (pathname === "/") return null;
  const service = SERVICES.find((s) => s.path === pagePath(pathname));
  if (service) return service.title;
  const cluster = CLUSTERS.find((c) => c.path === pagePath(pathname));
  if (cluster) return cluster.name;
  return STATIC_PAGE_TITLES[pathname.replace(/\/+$/, "")] ?? null;
};

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <header className="header" data-testid="site-header">
      <div className="scroll-progress" aria-hidden="true" />
      <div className="header-topbar">
        <div className="container header-topbar-inner">
          <a href={PHONE_HREF} className="topbar-phone" data-testid="link-topbar-phone">
            <PhoneIcon />
            <span>{PHONE_DISPLAY}</span>
          </a>
          <SocialLinks variant="topbar" />
        </div>
      </div>
      <div className="container header-inner">
        <Link to="/" className="header-logo" aria-label="J&S Audio Video Solutions, Home" data-testid="link-logo">
          <img src={logoSrc} alt="J&S Audio Video Solutions" width={500} height={373} fetchPriority="high" decoding="async" />
        </Link>

        {pageTitle && (
          <span className="header-page-label" data-testid="text-page-indicator">{pageTitle}</span>
        )}

        <div className="header-nav">
          <nav aria-label="Primary">
            <ul className="nav-links">
              {NAV_SERVICES.map((s) => (
                <li key={s.path}>
                  <Link to={s.path} className="nav-link" data-testid={s.testId}>{s.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
          <Link to="/contact" className="btn btn-sm" data-testid="link-header-cta" aria-label="Request a consultation from J&S Audio Video Solutions">
            Request a Consultation
          </Link>
        </div>

        <button
          type="button"
          className="mobile-menu-btn"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((m) => !m)}
          data-testid="button-mobile-toggle"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            {mobileOpen ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
          </svg>
        </button>
      </div>

      {mobileOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <ul>
            {NAV_SERVICES.map((s) => (
              <li key={s.path}>
                <Link to={s.path} className="nav-link" data-testid={`${s.testId}-mobile`}>{s.label}</Link>
              </li>
            ))}
            <li>
              <a href={PHONE_HREF} className="mobile-phone nav-link" data-testid="link-mobile-phone">
                <PhoneIcon />
                <span>{PHONE_DISPLAY}</span>
              </a>
            </li>
            <li><Link to="/contact" className="btn" style={{ width: "100%" }} data-testid="link-mobile-cta">Request a Consultation</Link></li>
            <li>
              <SocialLinks variant="header" />
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;
